import { VideoPresets, type TrackPublishDefaults, type VideoCaptureOptions } from 'livekit-client';

/**
 * Résolution demandée à la caméra du présentateur. Partagée entre l'aperçu de l'écran de
 * préparation et la publication : un périphérique ne sert qu'un seul format à la fois, donc si
 * l'aperçu l'ouvre sans contrainte, LiveKit hérite du format par défaut (souvent 640x480) et
 * calcule son budget d'encodage dessus.
 */
export const CAMERA_RESOLUTION = VideoPresets.h720.resolution;

export function buildCameraCaptureOptions(deviceId?: string): VideoCaptureOptions {
  return {
    resolution: CAMERA_RESOLUTION,
    ...(deviceId && { deviceId }),
  };
}

/** Contraintes de l'aperçu, alignées sur la publication pour que les deux ouvrent le même format. */
export function buildCameraPreviewConstraints(deviceId: string): MediaTrackConstraints {
  return {
    deviceId: { exact: deviceId },
    width: CAMERA_RESOLUTION.width,
    height: CAMERA_RESOLUTION.height,
    frameRate: CAMERA_RESOLUTION.frameRate,
  };
}

/**
 * Plafond de la couche haute. LiveKit vise 3 Mbps en 1080p ; mesuré au VMAF sur un plan de
 * présentateur, 2 Mbps en coûte 1,1 point, cinq fois sous le seuil de perception. On rend donc
 * 1 Mbps au lien montant sans perte visible, ce qui compte pour les présentateurs limités en montée.
 */
export const CAMERA_MAX_BITRATE = 2_000_000;

/**
 * Rabote les couches qui dépassent le plafond, sans toucher à l'échelle ni aux couches basses :
 * une webcam 480p reste à son budget dérivé (500 kbps), seule une vraie 1080p est concernée.
 * Renvoie `null` quand rien ne dépasse, pour éviter un `setParameters` inutile.
 *
 * Ne raisonne pas sur la source : c'est à l'appelant de ne pas l'appliquer au partage d'écran,
 * dont les 4 Mbps sont voulus.
 */
export function capCameraEncodings(
  encodings: RTCRtpEncodingParameters[] | undefined,
  max: number = CAMERA_MAX_BITRATE,
): RTCRtpEncodingParameters[] | null {
  if (!encodings || encodings.length === 0) return null;
  if (!encodings.some((e) => e.maxBitrate != null && e.maxBitrate > max)) return null;
  return encodings.map((e) =>
    e.maxBitrate != null && e.maxBitrate > max ? { ...e, maxBitrate: max } : e,
  );
}

export function buildCameraPublishOptions(): TrackPublishDefaults {
  return {
    // Sous congestion : on baisse la définition, jamais la cadence. Explicite car livekit-client
    // bascule seul en `maintain-resolution` dès que la hauteur demandée atteint 1080, ce qui fige
    // l'image du présentateur au lieu de la laisser bouger.
    degradationPreference: 'balanced',
    // Volontairement pas de `videoEncoding` ni de `videoSimulcastLayers` : LiveKit dérive le budget
    // de la résolution réellement capturée (3 Mbps en 1080p, 500 kbps pour une webcam 480p) et
    // reconstruit la même échelle 180/360/1080 que celle qu'on déclarait à la main.
  };
}
